import { EMAIL } from "@/lib/fda";
import { LEGAL_DISCLOSURE } from "@/lib/legal";

const Wrap = ({ title, testid, children }) => (
  <div className="px-5 py-16 lg:px-10" data-testid={testid}>
    <div className="mx-auto max-w-3xl">
      <h1 className="font-display text-4xl font-extrabold text-forest sm:text-5xl">{title}</h1>
      <div className="mt-10 space-y-5 text-sm leading-relaxed text-forest-soft">{children}</div>
    </div>
  </div>
);

const H = ({ children }) => <h2 className="pt-4 font-display text-xl font-bold text-forest">{children}</h2>;
const UL = ({ items }) => <ul className="list-disc space-y-2 pl-6">{items.map((t) => <li key={t}>{t}</li>)}</ul>;

export function PrivacyPolicy() {
  return (
    <Wrap title="Privacy policy" testid="privacy-policy-page">
      <H>What personal information do we collect?</H>
      <p>We will only collect information that we actually need, or where we’re required to collect the
        information to enable us to perform our legal, regulatory or contractual obligations necessary to provide
        you with a service, or where we have your permission.</p>
      <p>This will likely include the collection of:</p>
      <UL items={["Your details (e.g. Title, name, surname)", "Address details",
        "Contact details (e.g. Phone number, e-mail)", "Financial Information", "Employment Information",
        "information on how you use our website(s) and products and services", "Special Personal Information"]} />

      <H>Special Personal Information</H>
      <p>Data protection law defines some personal information as “special categories of data”. This includes
        information about physical or mental health, sexual orientation, religious beliefs, race or ethnic origin,
        political opinions, trade union membership or biometric data. It may be necessary to collect some data
        that falls into this category to ensure that we have a complete understanding of the reason for your
        financial circumstances or where it may help us to provide a better service to you.</p>
      <p>Where it is necessary to request this data, by disclosing this information to us, you are giving your
        explicit consent for us and any other third party to process this data as set out above.</p>

      <H>Information about other people</H>
      <p>If you provide personal information about someone else, for example when a joint enquiry is made, you
        must do so with the permission of the other person. We will use the personal information about the other
        person in the ways as described in this privacy policy.</p>

      <H>How do we collect your personal information?</H>
      <p>We collect your information in a number of ways.</p>
      <UL items={["When you make an enquiry to us either by phone, email, our website, by a third party or by any other means",
        "Where you have given permission for your information to be provided to us"]} />

      <H>How do we use your personal information?</H>
      <p>We will only collect and use your personal information in accordance with data protection laws. Our
        grounds for processing your personal information are as follows:</p>
      <p><strong>Consent – </strong>Where necessary we will only collect and process your personal information if
        you have given your consent for us to do so, for example, we will only send you certain marketing emails
        and process any sensitive information about you if we have your consent.</p>
      <p><strong>Legitimate Interests</strong> – We may use and process some of your personal information where we
        have sensible and legitimate business grounds for doing so. Under European privacy laws there is a concept
        of “legitimate interests” as a justification for processing your personal information. Our legitimate
        interests for processing your personal information are to:</p>
      <UL items={["Contact you in connection with your enquiry;", "Verify your identity",
        "Extract certain information", "To manage risk for us and our customers;",
        "To obey laws and regulations that apply to us;", "To respond to complaints and seek to resolve them;",
        "Update this website to better meet your needs in the future."]} />

      <H>What we do with the information we gather and Who the data will be shared with?</H>
      <p>Following your enquiry your information will be stored on our system and it may be also shared with our
        third-party associates upon your consent.</p>
      <p>We recommend reviewing their privacy policies which will detail how they will use your information. We
        may be paid an introductory fee if you opt for a solution from our third-party associates.</p>
      <p>Other third parties: We may also share your personal information where we have your consent to do so, or
        where we are required to do so under a legal or regulatory obligation, for example where we are required to
        do so by a court order, the police, local authorities or the courts. We might share some of your
        information with the emergency services if you are in any immediate danger.</p>

      <H>Access to your personal information</H>
      <p>You have the right to request from us a copy of the personal information that we may hold about you. This
        is often called a “Data Subject Access Request”.</p>
      <p>You can request this information by contacting us via email. We will provide this information free of
        charge.</p>

      <H>Right to have your personal information corrected</H>
      <p>If the personal information we hold about you is incorrect you have the right to request that we correct
        this.</p>

      <H>Right to stop or limit the processing of the data we carry out</H>
      <p>You may request that your personal information is deleted or that we stop processing the information if
        we’re no longer entitled to process it.</p>

      <H>How long do we keep your personal information for?</H>
      <p>We will keep a record of your personal information to ensure that we provide you with the best service
        possible and where we’re required to keep your personal information to meet our legal and regulatory
        obligations.</p>
      <p>Your information will be deleted after 3 years if you request the information to be deleted sooner
        contact us on <a className="font-semibold underline" href={`mailto:${EMAIL}`}>{EMAIL}</a></p>

      <p className="rounded-xl border border-border bg-white p-5">{LEGAL_DISCLOSURE}</p>
    </Wrap>
  );
}

export function CookiePolicy() {
  return (
    <Wrap title="Cookie Policy" testid="cookie-policy-page">
      <p>This website uses cookies and similar technologies. Necessary cookies are required for the website and
        the assessment to function, including keeping your progress through the assessment and protecting against
        duplicate submissions.</p>
      <p>Non-essential analytics and advertising cookies are only used where you have given consent using the
        cookie banner. You can change your choice at any time by clearing this site's data in your browser and
        reloading the page.</p>
      <p>Advertising measurement (including the Meta Pixel) is not enabled unless it has been configured by the
        business and you have accepted non-essential cookies. We never send your financial questionnaire answers,
        debt amounts, income or creditor information to any advertising platform.</p>
      <p>Questions about cookies: <a className="font-semibold underline" href={`mailto:${EMAIL}`}>{EMAIL}</a>.</p>
    </Wrap>
  );
}

export function Terms() {
  return (
    <Wrap title="Terms and Conditions" testid="terms-page">
      <p>You will be contacted by Freedom Debt Advice in regards to your initial assessment at whatever time you
        have requested to opt-out email us on <a className="font-semibold underline" href={`mailto:${EMAIL}`}>{EMAIL}</a></p>

      <div className="mt-10 rounded-xl border-2 border-dashed border-terracotta bg-terracotta/5 p-5 text-sm font-semibold text-forest" data-testid="terms-scope-notice">
        Note for the business (not part of the terms): the wording above is reproduced exactly as supplied and is
        the only Terms and Conditions wording provided. It does not yet cover the areas a full set of website
        terms would normally address — the service provided, no-advice/no-recommendation position, fees and
        referral fees, accuracy of information supplied by the customer, website availability, limitation of
        liability, intellectual property, complaints and governing law.
      </div>

      <p className="rounded-xl border border-border bg-white p-5">{LEGAL_DISCLOSURE}</p>
    </Wrap>
  );
}
